import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Lock,
  Plus,
  Trash2,
  Edit2,
  Check,
  X,
  Key,
  Terminal,
  Shield,
  ShieldOff,
  ShieldCheck,
  Globe,
  GitBranch,
  ChevronDown,
} from 'lucide-react';
import { useAuthenticatedApi } from '../../hooks/useAuthenticatedApi';
import { apiEndpoints } from '../../utils/api';
import {
  ApplicationRepository,
  ApplicationResponse,
  NetworkAccessMode,
  RepoEnvironmentVariable,
  RepositorySandboxConfig,
} from '../../types/application';
import RepositoryPrScanSettings, {
  normalizeRepositoryPrScanSettings,
  RepositoryPrScanSettingsValue,
} from './RepositoryPrScanSettings';
import { Spinner } from '../../design-system';
import PopoverPortal from '../common/PopoverPortal';
import type { GitProvider } from '../../config/providers';

type ConfigTab = 'environment' | 'setup_script' | 'network' | 'pull_requests';

const TABS: { id: ConfigTab; label: string; icon: React.ElementType }[] = [
  { id: 'environment', label: 'Environment', icon: Key },
  { id: 'setup_script', label: 'Setup Script', icon: Terminal },
  { id: 'network', label: 'Network', icon: Shield },
  { id: 'pull_requests', label: 'Pull Requests', icon: GitBranch },
];

interface RepositorySandboxConfigSectionProps {
  organizationId: string;
  applicationId: string;
  repositoryFullName: string;
  repositoryProvider: GitProvider;
  repository: ApplicationRepository;
  applicationRepositories: ApplicationRepository[];
  onApplicationUpdated: (updatedApplication: ApplicationResponse) => void;
  readOnly?: boolean;
}

const RepositorySandboxConfigSection: React.FC<RepositorySandboxConfigSectionProps> = ({
  organizationId,
  applicationId,
  repositoryFullName,
  repositoryProvider,
  repository,
  applicationRepositories,
  onApplicationUpdated,
  readOnly = false,
}) => {
  const api = useAuthenticatedApi();

  // Tab state
  const [activeTab, setActiveTab] = useState<ConfigTab>('environment');
  const [isTabPickerOpen, setIsTabPickerOpen] = useState(false);
  const tabPickerRef = useRef<HTMLDivElement>(null);


  const currentTabMeta = TABS.find((t) => t.id === activeTab) ?? TABS[0];

  // Config state
  const [config, setConfig] = useState<RepositorySandboxConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Environment variables state
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingEnvVarId, setEditingEnvVarId] = useState<string | null>(null);
  const [savingEnvVarId, setSavingEnvVarId] = useState<string | null>(null);
  const [deletingEnvVarId, setDeletingEnvVarId] = useState<string | null>(null);
  const [addingEnvVar, setAddingEnvVar] = useState(false);

  // New env var form
  const [newVarName, setNewVarName] = useState('');
  const [newVarValue, setNewVarValue] = useState('');
  const [newVarDescription, setNewVarDescription] = useState('');
  const [newVarEncrypted, setNewVarEncrypted] = useState(false);

  // Edit env var form
  const [editForm, setEditForm] = useState<{
    id: string;
    name: string;
    value: string;
    description: string;
    is_encrypted: boolean;
  } | null>(null);

  // Setup script state
  const [setupScript, setSetupScript] = useState('');
  const [savingScript, setSavingScript] = useState(false);
  const [scriptModified, setScriptModified] = useState(false);

  // Network access state
  const [networkMode, setNetworkMode] = useState<NetworkAccessMode>('deny_all');
  const [allowedHosts, setAllowedHosts] = useState<string[]>([]);
  const [newHost, setNewHost] = useState('');
  const [savingNetwork, setSavingNetwork] = useState(false);
  const [networkModified, setNetworkModified] = useState(false);

  // Pull request scan settings state
  const [repositoryPrScanSettings, setRepositoryPrScanSettings] = useState<RepositoryPrScanSettingsValue>(
    normalizeRepositoryPrScanSettings({
      pr_scan_enabled: repository.pr_scan_enabled,
      pr_scan_mode: repository.pr_scan_mode,
      pr_scan_engines: repository.pr_scan_engines,
    })
  );
  const [repositoryPrScanModified, setRepositoryPrScanModified] = useState(false);
  const [savingRepositoryPrScan, setSavingRepositoryPrScan] = useState(false);

  // Toast state
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const baseRepositoryPrScanSettings = normalizeRepositoryPrScanSettings({
    pr_scan_enabled: repository.pr_scan_enabled,
    pr_scan_mode: repository.pr_scan_mode,
    pr_scan_engines: repository.pr_scan_engines,
  });

  const hasPrScanChanges = (candidate: RepositoryPrScanSettingsValue) => {
    if (candidate.pr_scan_enabled !== baseRepositoryPrScanSettings.pr_scan_enabled) {
      return true;
    }
    if (candidate.pr_scan_mode !== baseRepositoryPrScanSettings.pr_scan_mode) {
      return true;
    }
    if (candidate.pr_scan_engines.length !== baseRepositoryPrScanSettings.pr_scan_engines.length) {
      return true;
    }
    return candidate.pr_scan_engines.some(
      (engine, index) => engine !== baseRepositoryPrScanSettings.pr_scan_engines[index]
    );
  };

  // ── Data loading ──────────────────────────────────────────────────────

  const loadConfig = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get<RepositorySandboxConfig>(
        apiEndpoints.repoSandboxConfig.get(organizationId, applicationId, repositoryFullName)
      );
      setConfig(response);
      setSetupScript(response.setup_script || '');
      setNetworkMode(response.network_access?.mode || 'deny_all');
      setAllowedHosts(response.network_access?.allowed_hosts || []);
      setNetworkModified(false);
    } catch (err: any) {
      console.error('Failed to load sandbox config:', err);
      setError('Failed to load sandbox configuration');
    } finally {
      setLoading(false);
    }
  }, [api, organizationId, applicationId, repositoryFullName]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  useEffect(() => {
    const normalized = normalizeRepositoryPrScanSettings({
      pr_scan_enabled: repository.pr_scan_enabled,
      pr_scan_mode: repository.pr_scan_mode,
      pr_scan_engines: repository.pr_scan_engines,
    });
    setRepositoryPrScanSettings(normalized);
    setRepositoryPrScanModified(false);
  }, [
    repository.pr_scan_enabled,
    repository.pr_scan_mode,
    repository.pr_scan_engines,
    repository.full_name,
    repository.provider,
  ]);

  // ── Environment variable handlers ─────────────────────────────────────

  const handleAddEnvVar = async () => {
    if (!newVarName || !newVarValue) {
      setToast({ message: 'Name and value are required', type: 'error' });
      return;
    }
    if (!/^[A-Z][A-Z0-9_]*$/.test(newVarName)) {
      setToast({
        message: 'Variable names must be uppercase letters, numbers, and underscores, starting with a letter',
        type: 'error',
      });
      return;
    }
    try {
      setAddingEnvVar(true);
      await api.post(
        apiEndpoints.repoSandboxConfig.envVars(organizationId, applicationId, repositoryFullName),
        { name: newVarName, value: newVarValue, description: newVarDescription || undefined, is_encrypted: newVarEncrypted }
      );
      setNewVarName('');
      setNewVarValue('');
      setNewVarDescription('');
      setNewVarEncrypted(false);
      setShowAddForm(false);
      await loadConfig();
      setToast({ message: 'Environment variable added', type: 'success' });
    } catch (err: any) {
      setToast({ message: err.message || 'Failed to add environment variable', type: 'error' });
    } finally {
      setAddingEnvVar(false);
    }
  };

  const startEditEnvVar = (envVar: RepoEnvironmentVariable) => {
    setEditingEnvVarId(envVar.id);
    setEditForm({ id: envVar.id, name: envVar.name, value: envVar.value || '', description: envVar.description || '', is_encrypted: envVar.is_encrypted });
  };

  const cancelEditEnvVar = () => {
    setEditingEnvVarId(null);
    setEditForm(null);
  };

  const saveEnvVarEdit = async () => {
    if (!editForm) return;
    if (editForm.name && !/^[A-Z][A-Z0-9_]*$/.test(editForm.name)) {
      setToast({ message: 'Variable names must be uppercase letters, numbers, and underscores, starting with a letter', type: 'error' });
      return;
    }
    try {
      setSavingEnvVarId(editForm.id);
      await api.put(
        apiEndpoints.repoSandboxConfig.envVar(organizationId, applicationId, repositoryFullName, editForm.id),
        { name: editForm.name, value: editForm.value || undefined, description: editForm.description || undefined, is_encrypted: editForm.is_encrypted }
      );
      setEditingEnvVarId(null);
      setEditForm(null);
      await loadConfig();
      setToast({ message: 'Environment variable updated', type: 'success' });
    } catch (err: any) {
      setToast({ message: err.message || 'Failed to update environment variable', type: 'error' });
    } finally {
      setSavingEnvVarId(null);
    }
  };

  const deleteEnvVar = async (envVarId: string) => {
    try {
      setDeletingEnvVarId(envVarId);
      await api.delete(apiEndpoints.repoSandboxConfig.envVar(organizationId, applicationId, repositoryFullName, envVarId));
      await loadConfig();
      setToast({ message: 'Environment variable deleted', type: 'success' });
    } catch (err: any) {
      setToast({ message: err.message || 'Failed to delete environment variable', type: 'error' });
    } finally {
      setDeletingEnvVarId(null);
    }
  };

  // ── Setup script handlers ─────────────────────────────────────────────

  const saveSetupScript = async () => {
    try {
      setSavingScript(true);
      await api.put(
        apiEndpoints.repoSandboxConfig.updateSetupScript(organizationId, applicationId, repositoryFullName),
        { setup_script: setupScript || null }
      );
      setScriptModified(false);
      await loadConfig();
      setToast({ message: 'Setup script saved', type: 'success' });
    } catch (err: any) {
      setToast({ message: err.message || 'Failed to save setup script', type: 'error' });
    } finally {
      setSavingScript(false);
    }
  };

  // ── Network access handlers ───────────────────────────────────────────

  const saveNetworkAccess = async () => {
    try {
      setSavingNetwork(true);
      await api.put(
        apiEndpoints.repoSandboxConfig.updateNetworkAccess(organizationId, applicationId, repositoryFullName),
        { mode: networkMode, allowed_hosts: networkMode === 'custom' ? allowedHosts : [] }
      );
      setNetworkModified(false);
      await loadConfig();
      setToast({ message: 'Network access settings saved', type: 'success' });
    } catch (err: any) {
      setToast({ message: err.message || 'Failed to save network access settings', type: 'error' });
    } finally {
      setSavingNetwork(false);
    }
  };

  const addHost = () => {
    const trimmed = newHost.trim();
    if (!trimmed) return;
    if (allowedHosts.includes(trimmed)) {
      setToast({ message: 'Host already in list', type: 'error' });
      return;
    }
    setAllowedHosts([...allowedHosts, trimmed]);
    setNewHost('');
    setNetworkModified(true);
  };

  const removeHost = (host: string) => {
    setAllowedHosts(allowedHosts.filter((h) => h !== host));
    setNetworkModified(true);
  };

  const handleNetworkModeChange = (mode: NetworkAccessMode) => {
    setNetworkMode(mode);
    setNetworkModified(true);
  };

  const saveRepositoryPrScanSettings = async () => {
    if (
      repositoryPrScanSettings.pr_scan_enabled &&
      repositoryPrScanSettings.pr_scan_engines.length === 0
    ) {
      setToast({
        message: 'Select at least one scan engine, or disable PR/MR scanning.',
        type: 'error',
      });
      return;
    }

    try {
      setSavingRepositoryPrScan(true);
      const updatedRepositories = applicationRepositories.map((existingRepository) => {
        const existingProvider = existingRepository.provider || 'github';
        if (
          existingRepository.full_name !== repositoryFullName ||
          existingProvider !== repositoryProvider
        ) {
          return existingRepository;
        }
        return {
          ...existingRepository,
          pr_scan_enabled: repositoryPrScanSettings.pr_scan_enabled,
          pr_scan_mode: repositoryPrScanSettings.pr_scan_mode,
          pr_scan_engines: repositoryPrScanSettings.pr_scan_engines,
        };
      });

      const updatedApplication = await api.put<ApplicationResponse>(
        apiEndpoints.applications.update(organizationId, applicationId),
        { repositories: updatedRepositories }
      );
      onApplicationUpdated(updatedApplication);
      setRepositoryPrScanModified(false);
      setToast({ message: 'Pull request scan settings saved', type: 'success' });
    } catch (saveError: any) {
      setToast({
        message: saveError.message || 'Failed to save pull request scan settings',
        type: 'error',
      });
    } finally {
      setSavingRepositoryPrScan(false);
    }
  };

  // ── Tab renderers ─────────────────────────────────────────────────────

  const envVars = config?.environment_variables || [];

  const renderEnvironmentTab = () => (
    <div>
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs text-ui-text-tertiary">
          Variables injected into the sandbox at startup.
        </p>
        {!showAddForm && (
          <button
            onClick={() => setShowAddForm(true)}
            disabled={readOnly}
            className="text-xs flex items-center gap-1 px-2 py-1 rounded bg-brand-action hover:bg-brand-action-hover text-white dark:text-black transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-brand-action"
          >
            <Plus className="w-3 h-3" />
            Add
          </button>
        )}
      </div>

      {showAddForm && (
        <div className="mb-4 p-3 border border-ui-border-primary rounded-lg bg-ui-background-secondary">
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-ui-text-secondary mb-1">Name</label>
                <input
                  type="text"
                  value={newVarName}
                  onChange={(e) => setNewVarName(e.target.value.toUpperCase())}
                  placeholder="MY_VAR"
                  className="w-full px-2 py-1.5 text-sm border border-ui-border-primary rounded bg-ui-background-secondary text-ui-text-primary"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-ui-text-secondary mb-1">Value</label>
                <input
                  type="text"
                  value={newVarValue}
                  onChange={(e) => setNewVarValue(e.target.value)}
                  placeholder="value"
                  className="w-full px-2 py-1.5 text-sm border border-ui-border-primary rounded bg-ui-background-secondary text-ui-text-primary font-mono"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-ui-text-secondary mb-1">Description (optional)</label>
              <input
                type="text"
                value={newVarDescription}
                onChange={(e) => setNewVarDescription(e.target.value)}
                placeholder="What is this variable for?"
                className="w-full px-2 py-1.5 text-sm border border-ui-border-primary rounded bg-ui-background-secondary text-ui-text-primary"
              />
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="encrypt-new" checked={newVarEncrypted} onChange={(e) => setNewVarEncrypted(e.target.checked)} className="w-3.5 h-3.5" />
              <label htmlFor="encrypt-new" className="text-xs text-ui-text-secondary">Encrypt value</label>
            </div>
            <div className="flex gap-2">
              <button onClick={handleAddEnvVar} disabled={addingEnvVar} className="px-3 py-1.5 text-xs bg-brand-action hover:bg-brand-action-hover disabled:opacity-50 text-white rounded flex items-center gap-1 transition-colors">
                {addingEnvVar ? <Spinner size={12} tone="current" /> : <Check className="w-3 h-3" />}
                Add
              </button>
              <button
                onClick={() => { setShowAddForm(false); setNewVarName(''); setNewVarValue(''); setNewVarDescription(''); setNewVarEncrypted(false); }}
                className="px-3 py-1.5 text-xs text-ui-text-secondary border border-ui-border-primary hover:bg-ui-background-tertiary rounded flex items-center gap-1 transition-colors"
              >
                <X className="w-3 h-3" />
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {envVars.length === 0 ? (
        <div className="text-xs text-ui-text-tertiary py-4 text-center">
          No environment variables configured
        </div>
      ) : (
        <div className="space-y-2">
          {envVars.map((envVar) => (
            <div key={envVar.id} className="p-2 border border-ui-border-primary rounded bg-ui-background-secondary">
              {editingEnvVarId === envVar.id && editForm ? (
                <div className="space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    <input type="text" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value.toUpperCase() })} className="px-2 py-1 text-xs border border-ui-border-primary rounded bg-ui-background-secondary text-ui-text-primary" />
                    <input type="text" value={editForm.value} onChange={(e) => setEditForm({ ...editForm, value: e.target.value })} placeholder="New value" className="px-2 py-1 text-xs border border-ui-border-primary rounded bg-ui-background-secondary text-ui-text-primary font-mono" />
                  </div>
                  <input type="text" value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} placeholder="Description" className="w-full px-2 py-1 text-xs border border-ui-border-primary rounded bg-ui-background-secondary text-ui-text-primary" />
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <input type="checkbox" checked={editForm.is_encrypted} onChange={(e) => setEditForm({ ...editForm, is_encrypted: e.target.checked })} className="w-3 h-3" />
                      <span className="text-xs text-ui-text-secondary">Encrypt</span>
                    </div>
                    <div className="flex gap-1">
                      <button onClick={saveEnvVarEdit} disabled={savingEnvVarId === envVar.id} className="p-1 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded">
                        {savingEnvVarId === envVar.id ? <Spinner size={12} tone="current" /> : <Check className="w-3 h-3" />}
                      </button>
                      <button onClick={cancelEditEnvVar} className="p-1 text-ui-text-tertiary hover:bg-ui-background-tertiary rounded">
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="font-mono text-xs font-medium text-ui-text-primary">{envVar.name}</span>
                    {envVar.is_encrypted && <span title="Encrypted"><Lock className="w-3 h-3 text-amber-500" /></span>}
                    <span className="text-xs text-ui-text-tertiary truncate">{envVar.is_encrypted ? '••••••••' : envVar.value}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => startEditEnvVar(envVar)}
                      disabled={readOnly}
                      className="p-1 text-ui-text-tertiary hover:bg-ui-background-tertiary rounded disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                    >
                      <Edit2 className="w-3 h-3" />
                    </button>
                    <button
                      onClick={() => deleteEnvVar(envVar.id)}
                      disabled={readOnly || deletingEnvVarId === envVar.id}
                      className="p-1 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                    >
                      {deletingEnvVarId === envVar.id ? <Spinner size={12} tone="current" /> : <Trash2 className="w-3 h-3" />}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderSetupScriptTab = () => (
    <div>
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs text-ui-text-tertiary">
          Shell script that runs when the sandbox starts, before fixes are applied.
        </p>
        {scriptModified && (
          <button
            onClick={saveSetupScript}
            disabled={savingScript || readOnly}
            className="text-xs flex items-center gap-1 px-2 py-1 rounded bg-brand-action hover:bg-brand-action-hover disabled:opacity-50 disabled:cursor-not-allowed text-white dark:text-black transition-colors"
          >
            {savingScript ? <Spinner size={12} tone="current" /> : <Check className="w-3 h-3" />}
            Save
          </button>
        )}
      </div>
      <textarea
        value={setupScript}
        onChange={(e) => {
          setSetupScript(e.target.value);
          setScriptModified(e.target.value !== (config?.setup_script || ''));
        }}
        disabled={readOnly}
        placeholder={'#!/bin/bash\n# Install dependencies, set up environment, etc.\nnpm install'}
        rows={6}
        className="w-full px-3 py-2 text-sm font-mono border border-ui-border-primary rounded bg-ui-background-secondary text-ui-text-primary resize-y disabled:opacity-60 disabled:cursor-not-allowed"
      />
    </div>
  );

  const renderNetworkTab = () => (
    <div>
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs text-ui-text-tertiary">
          Control outbound internet access for sandboxes running this repository.
        </p>
        {networkModified && (
          <button
            onClick={saveNetworkAccess}
            disabled={savingNetwork || readOnly}
            className="text-xs flex items-center gap-1 px-2 py-1 rounded bg-brand-action hover:bg-brand-action-hover disabled:opacity-50 disabled:cursor-not-allowed text-white dark:text-black transition-colors"
          >
            {savingNetwork ? <Spinner size={12} tone="current" /> : <Check className="w-3 h-3" />}
            Save
          </button>
        )}
      </div>

      <div className="space-y-2 mb-4">
        {([
          { mode: 'deny_all' as const, label: 'No Internet Access', description: 'Sandbox cannot make any outbound connections', Icon: ShieldOff, activeColor: 'red' },
          { mode: 'allow_all' as const, label: 'Full Internet Access', description: 'Sandbox can access any external host', Icon: Globe, activeColor: 'green' },
          { mode: 'custom' as const, label: 'Custom Allowlist', description: 'Only allow traffic to specific IPs, CIDRs, or domains', Icon: ShieldCheck, activeColor: 'blue' },
        ]).map(({ mode, label, description, Icon, activeColor }) => (
          <label
            key={mode}
            className={`flex items-center gap-3 p-2.5 rounded-lg border transition-colors ${
              networkMode === mode
                ? `border-${activeColor}-300 dark:border-${activeColor}-700 bg-${activeColor}-50 dark:bg-${activeColor}-900/20`
                : 'border-ui-border-primary hover:bg-ui-background-tertiary'
            } ${readOnly ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'}`}
          >
            <input type="radio" name="network-mode" checked={networkMode === mode} onChange={() => handleNetworkModeChange(mode)} disabled={readOnly} className="w-3.5 h-3.5" />
            <Icon className={`w-4 h-4 ${networkMode === mode ? `text-${activeColor}-500` : 'text-ui-text-tertiary'}`} />
            <div>
              <div className="text-xs font-medium text-ui-text-primary">{label}</div>
              <div className="text-xs text-ui-text-tertiary">{description}</div>
            </div>
          </label>
        ))}
      </div>

      {networkMode === 'custom' && (
        <div className="p-3 border border-ui-border-primary rounded-lg bg-ui-background-secondary">
          <div className="flex gap-2 mb-3">
            <input
              type="text"
              value={newHost}
              onChange={(e) => setNewHost(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addHost(); } }}
              disabled={readOnly}
              placeholder="e.g. api.example.com, 8.8.8.8, 10.0.0.0/8"
              className="flex-1 px-2 py-1.5 text-sm font-mono border border-ui-border-primary rounded bg-ui-background-secondary text-ui-text-primary disabled:opacity-60 disabled:cursor-not-allowed"
            />
            <button
              onClick={addHost}
              disabled={readOnly}
              className="px-3 py-1.5 text-xs bg-brand-action hover:bg-brand-action-hover text-white rounded flex items-center gap-1 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-brand-action"
            >
              <Plus className="w-3 h-3" />
              Add
            </button>
          </div>
          <p className="text-xs text-ui-text-tertiary mb-2">
            Supports IPs, CIDR blocks, and domains (including wildcards like *.example.com). Domain filtering works for HTTP/HTTPS traffic only.
          </p>
          {allowedHosts.length === 0 ? (
            <div className="text-xs text-ui-text-tertiary py-3 text-center">
              No hosts configured — all outbound traffic will be blocked
            </div>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {allowedHosts.map((host) => (
                <span key={host} className="inline-flex items-center gap-1 px-2 py-1 text-xs font-mono bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800 rounded">
                  {host}
                  <button
                    onClick={() => removeHost(host)}
                    disabled={readOnly}
                    className="text-blue-400 hover:text-red-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:text-blue-400"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );

  const renderPullRequestsTab = () => (
    <div>
      <div className='flex items-center justify-between mb-2'>
        <p className='text-xs text-ui-text-tertiary'>
          Configure pull/merge request triggered scans for this repository.
        </p>
        {repositoryPrScanModified && (
          <button
            onClick={saveRepositoryPrScanSettings}
            disabled={savingRepositoryPrScan || readOnly}
            className='text-xs flex items-center gap-1 px-2 py-1 rounded bg-brand-action hover:bg-brand-action-hover disabled:opacity-50 disabled:cursor-not-allowed text-white dark:text-black transition-colors'
          >
            {savingRepositoryPrScan ? (
              <Spinner size={12} tone="current" />
            ) : (
              <Check className='w-3 h-3' />
            )}
            Save
          </button>
        )}
      </div>

      <RepositoryPrScanSettings
        surface='plain'
        compact
        disabled={readOnly}
        value={repositoryPrScanSettings}
        onChange={(nextSettings) => {
          const normalized = normalizeRepositoryPrScanSettings(nextSettings);
          setRepositoryPrScanSettings(normalized);
          setRepositoryPrScanModified(hasPrScanChanges(normalized));
        }}
      />
    </div>
  );

  // ── Main render ───────────────────────────────────────────────────────

  return (
    <div className="mt-3">
      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-2 rounded-lg shadow-lg ${toast.type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'}`}>
          {toast.message}
        </div>
      )}

      {/* Tab picker — mobile (dropdown) */}
      <div className="md:hidden relative" ref={tabPickerRef}>
        <button
          type="button"
          onClick={() => setIsTabPickerOpen((v) => !v)}
          aria-haspopup="listbox"
          aria-expanded={isTabPickerOpen}
          className="w-full flex items-center justify-between gap-2 px-3 py-2 text-sm font-medium bg-ui-background-secondary border border-ui-border-primary rounded-lg text-ui-text-primary"
        >
          <span className="flex items-center gap-2 min-w-0">
            <currentTabMeta.icon className="w-4 h-4 text-brand-action flex-shrink-0" />
            <span className="truncate">{currentTabMeta.label}</span>
          </span>
          <ChevronDown
            className={`w-4 h-4 text-ui-text-tertiary transition-transform duration-150 flex-shrink-0 ${
              isTabPickerOpen ? 'rotate-180' : ''
            }`}
          />
        </button>

        <PopoverPortal
          anchorRef={tabPickerRef}
          open={isTabPickerOpen}
          onClose={() => setIsTabPickerOpen(false)}
          matchAnchorWidth
          className="bg-ui-background-secondary border border-ui-border-primary rounded-lg shadow-lg py-1 overflow-hidden"
        >
          <ul role="listbox">
            {TABS.map(({ id, label, icon: Icon }) => {
              const isActive = id === activeTab;
              return (
                <li key={id}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={isActive}
                    onClick={() => {
                      setActiveTab(id);
                      setIsTabPickerOpen(false);
                    }}
                    className={`w-full flex items-center gap-2 px-3 py-2.5 text-sm text-left transition-colors ${
                      isActive
                        ? 'bg-brand-action/10 text-ui-text-primary font-medium'
                        : 'text-ui-text-secondary hover:bg-ui-background-tertiary'
                    }`}
                  >
                    <Icon
                      className={`w-4 h-4 ${
                        isActive ? 'text-brand-action' : 'text-ui-text-tertiary'
                      }`}
                    />
                    <span className="flex-1">{label}</span>
                    {isActive && <Check className="w-4 h-4 text-brand-action" />}
                  </button>
                </li>
              );
            })}
          </ul>
        </PopoverPortal>
      </div>

      {/* Tab bar — desktop */}
      <div className="hidden md:flex border-b border-ui-border-primary">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setActiveTab(id)}
            className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium transition-colors border-b-2 -mb-px ${
              activeTab === id
                ? 'border-brand-action text-brand-action'
                : 'border-transparent text-ui-text-tertiary hover:text-ui-text-primary'
            }`}
          >
            <Icon className="w-3.5 h-3.5" />
            {label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="pt-4">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Spinner size={32} />
          </div>
        ) : error ? (
          <div className="text-red-500 text-sm">{error}</div>
        ) : (
          <>
            {activeTab === 'environment' && renderEnvironmentTab()}
            {activeTab === 'setup_script' && renderSetupScriptTab()}
            {activeTab === 'network' && renderNetworkTab()}
            {activeTab === 'pull_requests' && renderPullRequestsTab()}
          </>
        )}
      </div>
    </div>
  );
};

export default RepositorySandboxConfigSection;
